import React, { useState } from 'react';

import { VulnerabilitySeverity } from '@ariksa/inventory-core';
import {
  Box,
  BoxProps,
  Center,
  HStack,
  Stack,
  useDisclosure,
} from '@chakra-ui/react';
import { map } from 'lodash';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { customTheme } from '../../../../../../theme';

import {
  renderTooltipFieldValue,
  Tag,
  WithResourceIcon,
} from 'components/DataDisplay';
import { PlusCountTag } from 'components/DataDisplay/Tag/PlusCountTag';
import { GrayIconButton, OutlinedIconButton, PrimaryButton } from 'components/DataEntry';
import { IconTypes, ThirdPartyIcon } from 'components/Icons';
import { DashboardWidget } from 'components/Visualization/CDashboard/Widget/DashboardWidget';
import { ActiveResourceProvider } from 'containers/ActiveCloudResource/hooks/context/ActiveResourceProvider';
import { renderEmptyRows } from 'containers/Dashboard/utils/utils';
import { renderCloudEntities } from 'containers/Visibility/Vulnerabilities/Components/hooks/utils';
import { HostsDrawer } from 'containers/Visibility/Vulnerabilities/Components/VulnerabilityTable/HostsDrawer';
import { selectVulnerabilities } from 'containers/Visibility/Vulnerabilities/selectors';
import { VulnerabilityViewByTabs } from 'containers/Visibility/Vulnerabilities/types';

interface Props {
  setShow: (value: string) => void;
}

export const PatchablePackages: React.FC<Props> = props => {
  const { setShow } = props;
  const { patchablePackages, useVulnerabilityV3 } = useSelector(
    selectVulnerabilities,
  );
  const detailsDisclosure = useDisclosure();
  const [currentRecord, setCurrentRecord] = useState<Record<string, any>>({});
  const navigate = useNavigate();

  const textOverflowStyles: BoxProps = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    w: 'full',
  };

  return (
    <ActiveResourceProvider>
      <DashboardWidget
        label="Patchable Packages with Critical And High CVEs"
        isLoading={patchablePackages.isLoading}
        noDataMessage="No patchable packages found"
        noData={!patchablePackages.data?.length}
        styles={{ contentWrapper: { pb: 0 } }}
        rightHeader={
          <GrayIconButton
            leftIcon={<ThirdPartyIcon />}
            onClick={() => {
              setShow('Details');
              navigate(
                `/visibility/vulnerability?show=Details&tab=${VulnerabilityViewByTabs.Package}` +
                  `&isPatchable=yes&severities=${VulnerabilitySeverity.Critical},${VulnerabilitySeverity.High}`,
              );
            }}
            iconSize={6}
          >
            View all
          </GrayIconButton>
        }
        content={
          <>
            <Stack w="full" h="full" spacing={1} overflow="scroll">
              <HStack
                w="full"
                spacing={6}
                color={customTheme.colors.gray['300']}
                px={2}
              >
                <Box w="45%" pl={10}>
                  PACKAGE
                </Box>
                <Box w="35%">CVE</Box>
                <Box w="20%">ENTITIES</Box>
              </HStack>
              <Box
                borderBottom="1px solid"
                borderColor={customTheme.colors.gray['200']}
                fontSize="md"
              />
              <Stack
                w="full"
                h="full"
                justify="space-between"
                pb={2}
                fontSize="md"
                overflow="scroll"
                px={2}
              >
                {map(patchablePackages.data, (o, index) => (
                  <Center
                    w="full"
                    h="full"
                    key={'eol-key-' + index}
                    onClick={() => {
                      detailsDisclosure.onOpen();
                      setCurrentRecord(o);
                    }}
                    cursor="pointer"
                  >
                    <HStack w="full" spacing={6}>
                      <Box w="45%" overflow="hidden">
                        <WithResourceIcon
                          resourceType={
                            /*o?.package_category_name ||
                            o?.package_category ||*/
                            IconTypes.Packages
                          }
                          iconStyles={{ color: 'primary' }}
                          tooltip={
                            <Stack>
                              {renderTooltipFieldValue('Name', o?.package_name)}
                              {renderTooltipFieldValue(
                                'Version',
                                o?.installed_version,
                              )}
                            </Stack>
                          }
                        >
                          <Stack spacing={0} w="full">
                            <Box color="primary" {...textOverflowStyles}>
                              {o?.package_name}
                            </Box>
                            <Box {...textOverflowStyles}>
                              {o?.installed_version}
                            </Box>
                          </Stack>
                        </WithResourceIcon>
                      </Box>
                      <Box w="35%">
                        <HStack w="full" align="flex-start">
                          {o?.cve_ids?.[0] && <Box>{o?.cve_ids?.[0]}</Box>}
                          {!!o?.cve_ids?.length && o?.cve_ids?.length > 1 && (
                            <PlusCountTag count={o?.cve_ids?.length - 1} />
                          )}
                        </HStack>
                      </Box>
                      <Box w="20%">
                        {renderCloudEntities({
                          value: o?.entities,
                          showTotal: useVulnerabilityV3,
                        })}
                      </Box>
                    </HStack>
                  </Center>
                ))}
                {renderEmptyRows(patchablePackages.data?.length)}
              </Stack>
            </Stack>

            {detailsDisclosure.isOpen && (
              <HostsDrawer
                row={currentRecord}
                isOpen={detailsDisclosure.isOpen}
                onClose={detailsDisclosure.onClose}
                selectedTab={VulnerabilityViewByTabs.Package}
              />
            )}
          </>
        }
      />
    </ActiveResourceProvider>
  );
};
